"use client";

import type { ReactNode } from "react";
import { QuizStepShell } from "./QuizStepShell";
import { STEP_IMAGES, type StepImageEntry } from "./QuizStepImage";
import { SegmentedRadioGroup } from "@/components/ui/SegmentedRadioGroup";
import { InfoTooltip } from "@/components/ui/InfoTooltip";
import type { PriorityDescriptions } from "@/lib/api/recommendations/priorityDescriptions";

import { DIMENSIONS } from "./quizSchemas";
import type { PrioritiesDraft } from "./quizSchemas";
export type { PrioritiesDraft };

const PRIO_OPTS = [
  { v: 0, label: "Low"       },
  { v: 1, label: "A little"  },
  { v: 2, label: "Important" },
  { v: 3, label: "Essential" },
] as const;

// Radix RadioGroup requires string values; we bridge to/from the numeric schema.
const PRIO_OPTS_STR = PRIO_OPTS.map((o) => ({ label: o.label, value: String(o.v) }));

export function QuizStepPriorities({
  value,
  onChange,
  onNext,
  onBack,
  nextDisabled,
  disabled,
  // Defaults to the real pool in its declared order (not shuffled) — only
  // QuizWizard passes a real, session-shuffled order in production;
  // callers that don't care about images (most component tests) can omit
  // this entirely, matching the old imageIndex?'s ergonomics.
  imageOrder = STEP_IMAGES.priorities,
  imagePriority,
  banner,
  descriptions,
}: {
  value: PrioritiesDraft;
  onChange: (next: PrioritiesDraft) => void;
  onNext: () => void;
  onBack?: () => void;
  nextDisabled?: boolean;
  disabled?: boolean;
  imageOrder?: StepImageEntry[];
  imagePriority?: boolean;
  banner?: ReactNode;
  descriptions?: PriorityDescriptions;
}) {
  return (
    <QuizStepShell
      step="Step 01 of 02"
      imageOrder={imageOrder}
      imagePriority={imagePriority}
      title="Tell us what matters most to you on safari"
      subtitle="No wrong answers. Every choice helps us understand your perfect safari a little better."
      nav={{ onNext, onBack, nextDisabled, nextLabel: "Next" }}
      banner={banner}
    >
      {/* mt-8 is breathing room below the header — only meaningful on
          mobile, where the header sits directly above here in flow order.
          On desktop the header moved above the photo instead (see
          PhotoSplit.tsx), so md:mt-0 removes what would otherwise be an
          orphaned gap above the first row. */}
      <div className="mt-8 md:mt-0">
        {DIMENSIONS.map((d) => {
          const current = value[d.key];
          return (
            <div
              key={d.key}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 border-b border-border"
            >
              <div className="flex items-center gap-1.5 text-base font-semibold text-text">
                {d.label}
                <InfoTooltip description={descriptions?.[d.key] ?? ""} label={d.label} />
              </div>
              <SegmentedRadioGroup
                ariaLabel={d.label}
                options={PRIO_OPTS_STR}
                value={String(current)}
                onChange={(v) => onChange({ ...value, [d.key]: Number(v) })}
                disabled={disabled}
              />
            </div>
          );
        })}
      </div>
    </QuizStepShell>
  );
}
